import axios from 'axios'
import { platformEnv } from '@/platform/config/env'

const CLIENT_ID = (platformEnv.ssoClientId || 'shop').trim()
const ACCESS_TOKEN = `${CLIENT_ID}_access_token`
const REFRESH_TOKEN = `${CLIENT_ID}_refresh_token`
const EXPIRES_AT = `${CLIENT_ID}_token_expires_at`

const SSO_ACCESS_TOKEN = 'sso_access_token'
const SSO_REFRESH_TOKEN = 'sso_refresh_token'
const SSO_EXPIRES_AT = 'sso_token_expires_at'

export interface ClientTokenClaims {
  sub?: string
  email?: string
  type?: string
  client?: string
  role?: string
  exp?: number
  permissions?: unknown
}

export function getRootDomain(hostname: string): string | null {
  if (!hostname || hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return null
  }
  const parts = hostname.split('.')
  if (parts.length < 2) return null
  return parts.slice(-2).join('.')
}

export function isSameRootDomain(url1: string, url2: string): boolean {
  try {
    const a = new URL(url1, window.location.origin)
    const b = new URL(url2, window.location.origin)
    const rootA = getRootDomain(a.hostname)
    const rootB = getRootDomain(b.hostname)
    return rootA !== null && rootA === rootB
  } catch {
    return false
  }
}

function getCookieDomain(): string | undefined {
  const root = getRootDomain(window.location.hostname)
  return root ? `.${root}` : undefined
}

function getCookie(name: string): string | null {
  const value = document.cookie.split('; ').find((item) => item.startsWith(`${name}=`))?.split('=').slice(1).join('=')
  return value ? decodeURIComponent(value) : null
}

function setCookie(name: string, value: string, maxAge?: number, domain?: string): void {
  const attributes = [`${name}=${encodeURIComponent(value)}`, 'Path=/', 'SameSite=Lax']
  if (window.location.protocol === 'https:') attributes.push('Secure')
  if (domain) attributes.push(`Domain=${domain}`)
  if (maxAge !== undefined) attributes.push(`Max-Age=${maxAge}`)
  document.cookie = attributes.join('; ')
}

function deleteCookie(name: string, domain?: string): void {
  const attributes = [`${name}=`, 'Path=/', 'Max-Age=0']
  if (window.location.protocol === 'https:') attributes.push('Secure')
  if (domain) attributes.push(`Domain=${domain}`)
  document.cookie = attributes.join('; ')
}

export function getAccessToken(): string | null {
  // 1. Luôn ưu tiên token theo đúng Client ID (vd: shop_access_token)
  const clientToken = getCookie(ACCESS_TOKEN)
  if (clientToken) return clientToken

  // 2. Fallback token SSO toàn cục
  const ssoToken = getCookie(SSO_ACCESS_TOKEN)
  if (ssoToken) {
    const claims = readClientTokenClaims(ssoToken)
    if (claims) return ssoToken
  }

  return null
}

export function getRefreshToken(): string | null {
  return getCookie(REFRESH_TOKEN) || getCookie(SSO_REFRESH_TOKEN)
}

export function readClientTokenClaims(token = getAccessToken()): ClientTokenClaims | null {
  if (!token) return null
  try {
    const segment = token.split('.')[1]
    if (!segment) return null
    const normalized = segment.replace(/-/g, '+').replace(/_/g, '/')
    const payload = JSON.parse(atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '='))) as ClientTokenClaims
    return payload
  } catch {
    return null
  }
}

export function clearSessionTokens(): void {
  const domain = getCookieDomain()
  for (const name of [
    ACCESS_TOKEN, REFRESH_TOKEN, EXPIRES_AT,
    SSO_ACCESS_TOKEN, SSO_REFRESH_TOKEN, SSO_EXPIRES_AT,
    'ecom_access_token', 'ecom_refresh_token', 'ecom_token_expires_at',
  ]) {
    deleteCookie(name)
    if (domain) deleteCookie(name, domain)
  }
}

function saveTokens(payload: { access_token: string; refresh_token: string; expires_in: number }): void {
  const domain = getCookieDomain()
  const expiresAt = new Date(Date.now() + payload.expires_in * 1000).toISOString()

  // 1. Luôn lưu cookie cho host hiện tại (bắt buộc cho localhost và mọi domain)
  setCookie(ACCESS_TOKEN, payload.access_token, payload.expires_in)
  setCookie(REFRESH_TOKEN, payload.refresh_token, 7 * 24 * 60 * 60)
  setCookie(EXPIRES_AT, expiresAt, payload.expires_in)

  // 2. Nếu ở trên root domain chia sẻ (.hocnhe.com), lưu thêm với Domain để các subdomain khác cùng đọc được
  if (domain) {
    setCookie(ACCESS_TOKEN, payload.access_token, payload.expires_in, domain)
    setCookie(REFRESH_TOKEN, payload.refresh_token, 7 * 24 * 60 * 60, domain)
    setCookie(EXPIRES_AT, expiresAt, payload.expires_in, domain)
  }
}

export function isTokenExpiring(bufferSeconds = 60): boolean {
  const expiresAt = getCookie(EXPIRES_AT) || getCookie(SSO_EXPIRES_AT)
  if (!expiresAt) {
    const claims = readClientTokenClaims()
    if (claims?.exp) {
      return Date.now() + bufferSeconds * 1000 >= claims.exp * 1000
    }
    return true
  }
  const expiration = new Date(expiresAt).getTime()
  return Number.isNaN(expiration) || Date.now() + bufferSeconds * 1000 >= expiration
}

let activeRefreshPromise: Promise<string | null> | null = null

export async function refreshAccessToken(): Promise<string | null> {
  if (activeRefreshPromise) return activeRefreshPromise
  activeRefreshPromise = (async () => {
    const refreshToken = getRefreshToken()
    if (!refreshToken) return null
    try {
      const response = await axios.post(`${platformEnv.ssoApiUrl}/v1/auth/refresh`, { refresh_token: refreshToken }, { timeout: 10_000 })
      const payload = response.data?.data
      if (!response.data?.success || !payload?.access_token || !payload?.refresh_token || !payload?.expires_in) return null
      saveTokens(payload)
      return payload.access_token as string
    } catch (cause) {
      if (axios.isAxiosError(cause) && [400, 401, 403].includes(cause.response?.status ?? 0)) clearSessionTokens()
      return null
    } finally {
      activeRefreshPromise = null
    }
  })()
  return activeRefreshPromise
}

export async function getValidAccessToken(): Promise<string | null> {
  const clientToken = getCookie(ACCESS_TOKEN)
  if (clientToken && !isTokenExpiring()) return clientToken

  // Thử refresh token nếu có refresh token
  const refreshed = await refreshAccessToken()
  if (refreshed) return refreshed

  // Nếu chưa có shop_access_token nhưng có sso_access_token (chia sẻ từ sso.hocnhe.com):
  // Chủ động lấy token riêng cho client 'shop'
  const ssoToken = getCookie(SSO_ACCESS_TOKEN)
  if (ssoToken) {
    try {
      const response = await axios.post(`${platformEnv.ssoApiUrl}/v1/auth/client-token`, { client_id: CLIENT_ID }, {
        headers: { Authorization: `Bearer ${ssoToken}` },
        timeout: 10_000,
      })
      const payload = response.data?.data
      if (response.data?.success && payload?.access_token) {
        saveTokens(payload)
        return payload.access_token as string
      }
    } catch {
      // ignore
    }
    return ssoToken
  }

  return clientToken ?? null
}

export function redirectToLogin(returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`): void {
  const safeReturn = returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '/'
  const sameDomain = isSameRootDomain(window.location.href, platformEnv.ssoFrontendUrl)
  const login = new URL('/auth/login', platformEnv.ssoFrontendUrl)

  login.searchParams.set('sso-client-id', CLIENT_ID)

  if (sameDomain) {
    const targetUrl = new URL(safeReturn, window.location.origin).href
    login.searchParams.set('redirect_url', targetUrl)
  } else {
    const callback = new URL('/auth/callback', window.location.origin)
    callback.searchParams.set('returnTo', safeReturn)
    login.searchParams.set('redirect_url', callback.href)
  }

  window.location.assign(login.href)
}

export async function exchangeAuthorizationCode(code: string): Promise<void> {
  if (getAccessToken()) return

  const response = await axios.post(`${platformEnv.ssoApiUrl}/v1/auth/exchange-code`, { code }, { timeout: 10_000 })
  const payload = response.data?.data
  if (!response.data?.success || !payload?.access_token || !payload?.refresh_token || !payload?.expires_in) {
    throw new Error(response.data?.message || 'Không thể xác thực với SSO.')
  }
  saveTokens(payload)
}
